import './polyfills';

import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { isProdMode, versionInfo, apiBaseUrl } from './config';
import { apiProviders } from './api';
import { serviceProviders } from './services';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { ServerErrorInterceptor } from './core/interceptors/server-error.interceptor';
import { ServerErrorGuard } from './core/guards/server-error.guard';
import {
  StorageService,
  UserPreferencesService,
  AppGuard,
  MultipleTranslatePoHttpLoader,
  UserRightsModule,
  ShellV2Module,
  FormsRootModuleV2,
  ModalModule,
  SideDrawerModule,
  SpinnerV2Module,
  FileUploadV2Module,
  AssetsModule as AssetModule,
  PagesV2Module,
  UnitsModule,
} from '@schenck/rotec-components';
import { provideAnimations } from '@angular/platform-browser/animations';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { withHashLocation, provideRouter, Routes } from '@angular/router';
import { PagesComponent } from './pages/pages.component';
import { routes } from './routes';
import { serviceUnavailableRoutes } from './components/service-unavailable/service-unavailable.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { environment } from './environment/environment';
import { AppComponent } from './app.component';
import { bootstrapApplication } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';

import deLocale from '@angular/common/locales/de';
import enLocale from '@angular/common/locales/en';
import esLocale from '@angular/common/locales/es';
import frLocale from '@angular/common/locales/fr';
import itLocale from '@angular/common/locales/it';
import nlLocale from '@angular/common/locales/nl';
import svLocale from '@angular/common/locales/sv';
import plLocale from '@angular/common/locales/pl';
import trLocale from '@angular/common/locales/tr';
import zhLocale from '@angular/common/locales/zh';
import huLocale from '@angular/common/locales/hu';
import csLocale from '@angular/common/locales/cs';
import koLocale from '@angular/common/locales/ko';
import jaLocale from '@angular/common/locales/ja';
import ptLocale from '@angular/common/locales/pt';

registerLocaleData(deLocale, 'de');
registerLocaleData(enLocale, 'en');
registerLocaleData(esLocale, 'es');
registerLocaleData(frLocale, 'fr');
registerLocaleData(itLocale, 'it');
registerLocaleData(nlLocale, 'nl');
registerLocaleData(svLocale, 'sv');
registerLocaleData(plLocale, 'pl');
registerLocaleData(trLocale, 'tr');
registerLocaleData(zhLocale, 'zh');
registerLocaleData(huLocale, 'hu');
registerLocaleData(csLocale, 'cs');
registerLocaleData(koLocale, 'ko');
registerLocaleData(jaLocale, 'ja');
registerLocaleData(ptLocale, 'pt');

export function HttpPoLoaderFactory(http: HttpClient) {
  return new MultipleTranslatePoHttpLoader(http, [
    { prefix: 'assets/rotec-locales/', suffix: '.po' },
    { prefix: 'assets/locales/', suffix: '.po' },
  ]);
}

const appRoutes: Routes = [
  {
    path: '',
    component: PagesComponent,
    canActivate: [AppGuard],
    children: [
      { path: 'postlogout', redirectTo: '' },
      {
        path: routes.assets.url,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/assets/assets.component').then((m) => m.AssetsComponent),
          },
        ],
      },
      {
        path: routes.groups.url,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/groups/groups.component').then((m) => m.GroupsComponent),
          },
        ],
      },
      {
        path: routes.plants.url,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/plants/plants.component').then((m) => m.PlantsComponent),
          },
        ],
      },
      {
        path: routes.serviceHelpDesk.url,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/service-help-desk/service-help-desk/service-help-desk.component').then(
                (m) => m.ServiceHelpDeskComponent
              ),
          },
        ],
      },
      {
        path: routes.unhandledError.url,
        children: serviceUnavailableRoutes,
      },
      { path: '**', redirectTo: routes.assets.url },
    ],
  },
];

if (isProdMode) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpPoLoaderFactory,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(
      DragDropModule,
      // Rotec-Components Library
      UserRightsModule.forRoot(environment.umBaseUrl),
      ShellV2Module.forRoot({
        isProduction: isProdMode,
        appLogoAndName: { name: 'Asset Manager', logoUrl: './assets/img/assetmanager-white.png' },
        versionInfo,
        msalConfig: environment.msalConfig,
        azureEndpoint: environment.azureEndpoint,
        baseUrl: environment.apiBaseUrl,
        usBaseUrl: environment.usBaseUrl,
      }),
      FormsRootModuleV2,
      ModalModule.forRoot(),
      SideDrawerModule.forRoot(),
      SpinnerV2Module.forRoot(),
      FileUploadV2Module.forRoot({
        uploadBaseUrl: apiBaseUrl,
        fallbackImgSrcForFileUpload: './assets/rotor-data-center.svg',
        fallbackImgSrcForPipe: './assets/img/schenckone-hoch.png',
        uploadApi: 'files',
      }),
      AssetModule.forRoot(apiBaseUrl),
      // Own
      PagesV2Module,
      UnitsModule.forRoot()
    ),
    ...apiProviders,
    ...serviceProviders,
    { provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true },
    ServerErrorGuard,
    StorageService,
    UserPreferencesService,
    provideAnimations(),
    provideRouter(appRoutes, withHashLocation()),
    // Angular CDK overlays (used by rotec-components modals) default to rendering via the
    // native Popover API, which paints in the browser's top layer above ALL regular DOM
    // content, regardless of z-index. Third-party widgets that append elements outside
    // Angular's control (e.g. Google Places Autocomplete's suggestion dropdown) can then
    // never appear above an open modal. Disabling usePopover restores the classic
    // z-index-based overlay stacking so such widgets can be layered correctly again.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
  ],
}).catch((err) => console.log(err));
